import { Component, OnInit, Input } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-rm-concierge-chat',
  templateUrl: './rm-concierge-chat.component.html',
  styleUrls: ['./rm-concierge-chat.component.scss']
})
export class RmConciergeChatComponent implements OnInit {
  @Input() profileId: string = '';

  loading = true;
  conciergeData: any = null;
  messageText = '';
  sending = false;

  // Interactive Candidate Modal properties
  showCandidateModal = false;
  activeCandidate: any = null;
  loadingCandidateDetails = false;
  candidateModalTab = 'overview';
  rejectionReason = '';
  showRejectionInput = false;
  questionToRm = '';
  showQuestionInput = false;

  constructor(private http: HttpClient, private router: Router) {}

  private headers(): HttpHeaders {
    const token = localStorage.getItem('weddingly_token') || localStorage.getItem('x-access-token') || '';
    return new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` });
  }

  ngOnInit(): void {
    if (!this.profileId) {
      const userStr = localStorage.getItem('user_details') || localStorage.getItem('user') || 'null';
      try {
        const u = JSON.parse(userStr);
        if (u) this.profileId = u._id || u.profile_id || u.id;
      } catch {}
    }
    if (this.profileId) {
      this.loadConciergeDetails();
    } else {
      this.loading = false;
    }
  }

  loadConciergeDetails(): void {
    this.loading = true;
    this.http.get<any>(`${environment.apiUrl}/matches/rm-concierge/${this.profileId}`, { headers: this.headers() }).subscribe({
      next: res => {
        this.loading = false;
        if (res.status === 'success') {
          this.conciergeData = res.data;
        }
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  sendMessage(): void {
    if (!this.messageText.trim() || !this.profileId) return;
    this.sending = true;
    const text = this.messageText.trim();
    this.messageText = '';

    this.http.post<any>(`${environment.apiUrl}/matches/rm-concierge/${this.profileId}/send-message`, {
      content: text,
      message_type: 'TEXT'
    }, { headers: this.headers() }).subscribe({
      next: res => {
        this.sending = false;
        if (res.status === 'success') {
          if (this.conciergeData && this.conciergeData.messages) {
            this.conciergeData.messages.push(res.data);
          }
        }
      },
      error: () => {
        this.sending = false;
      }
    });
  }

  approveProfile(targetProfileId: string): void {
    this.http.post<any>(`${environment.apiUrl}/matches/rm-concierge/${this.profileId}/approve-profile`, {
      target_profile_id: targetProfileId
    }, { headers: this.headers() }).subscribe({
      next: res => {
        if (res.status === 'success') {
          alert('Profile approved! Your dedicated Relationship Manager has been notified and will initiate contact on your behalf immediately.');
          this.loadConciergeDetails();
        } else {
          alert(res.message || 'Approval failed');
        }
      }
    });
  }

  openCandidateModal(card: any): void {
    if (!card) return;
    const targetId = card._id || card.profile_id;
    if (!targetId) return;

    // Notify server of view in background
    this.http.post<any>(`${environment.apiUrl}/matches/rm-concierge/${this.profileId}/recommendation-action`, {
      action: 'VIEW',
      target_profile_id: targetId
    }, { headers: this.headers() }).subscribe();

    // Navigate directly to standard Profile Details page just like normal users see
    this.router.navigate(['/profile-details', targetId]);
  }

  closeCandidateModal(): void {
    this.showCandidateModal = false;
    this.showRejectionInput = false;
    this.showQuestionInput = false;
  }

  performRecommendationAction(action: 'INTEREST' | 'SAVE' | 'REJECT' | 'ASK_QUESTION', card?: any, customReason?: string, customQuestion?: string): void {
    const target = card || this.activeCandidate;
    if (!target) return;
    const targetId = target._id || target.profile_id;

    if (action === 'REJECT' && !customReason && !this.showRejectionInput) {
      this.showRejectionInput = true;
      this.showQuestionInput = false;
      return;
    }
    if (action === 'ASK_QUESTION' && !customQuestion && !this.showQuestionInput) {
      this.showQuestionInput = true;
      this.showRejectionInput = false;
      return;
    }

    this.http.post<any>(`${environment.apiUrl}/matches/rm-concierge/${this.profileId}/recommendation-action`, {
      action,
      target_profile_id: targetId,
      reason: customReason || this.rejectionReason,
      question_text: customQuestion || this.questionToRm
    }, { headers: this.headers() }).subscribe({
      next: res => {
        if (res.status === 'success') {
          if (action === 'INTEREST') {
            alert('Official Interest sent! Your dedicated RM has been notified and will initiate contact with the candidate and their family immediately.');
            target.interest_sent = true;
          } else if (action === 'SAVE') {
            alert('Candidate profile saved to your favorites list!');
            target.saved = true;
          } else if (action === 'REJECT') {
            alert('Recommendation dismissed. We have notified your RM to refine future recommendations according to your feedback.');
            target.rejected = true;
            this.closeCandidateModal();
          } else if (action === 'ASK_QUESTION') {
            alert('Your question has been sent directly into your Relationship Manager concierge chat room!');
            if (res.data && this.conciergeData && this.conciergeData.messages) {
              this.conciergeData.messages.push(res.data);
            }
            this.questionToRm = '';
            this.showQuestionInput = false;
          }
        } else {
          alert(res.message || 'Could not process action');
        }
      },
      error: () => {
        alert('Could not process recommendation action right now.');
      }
    });
  }

  /** Called by the "Not Interested" button directly on a chat card */
  showRejectionInputForCard(card: any, event?: Event): void {
    if (event) event.stopPropagation();
    const reason = prompt('Please briefly let your RM know why this profile is not suitable (e.g. Age mismatch, location preference):');
    if (reason !== null) {
      this.performRecommendationAction('REJECT', card, reason.trim() || 'Not specified');
    }
  }

  /** Called by the "Ask RM" button directly on a chat card */
  askRmAboutCard(card: any, event?: Event): void {
    if (event) event.stopPropagation();
    const shortId = (card._id || card.profile_id)?.toString().slice(-6)?.toUpperCase() || '';
    this.messageText = `Regarding recommendation ${card.name} (ID: #${shortId}): `;
    setTimeout(() => {
      const inputEl = document.querySelector('.chat-input-row input') as HTMLInputElement;
      if (inputEl) {
        inputEl.focus();
      }
    }, 100);
  }

  getSharedProfileCount(): number {
    if (!this.conciergeData || !this.conciergeData.messages) return 0;
    let count = 0;
    for (const msg of this.conciergeData.messages) {
      if (msg.message_type === 'PROFILE_CARD' && Array.isArray(msg.shared_profiles)) {
        count += msg.shared_profiles.length;
      }
    }
    return count;
  }
}
